"use client";

import * as React from "react";
import { Eye, EyeOff } from "lucide-react";

import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";
import {
  AUTH_LABEL_STYLE,
  AUTH_PLACEHOLDER_COLOR,
} from "./auth-tokens";
import { FieldError } from "./field-error";

/**
 * Labelled password input with a show/hide toggle, an inline validation
 * message and an optional requirement checklist.
 *
 * Previously re-implemented byte-for-byte in create-account-form.tsx and
 * reset-password-form.tsx (and needed in change-password-modal.tsx too), which
 * is why the QA fixes for password validation had to be made three times. This
 * is the single implementation.
 */
export function PasswordField({
  id,
  label,
  value,
  onChange,
  show,
  onToggle,
  autoComplete,
  placeholder = "輸入密碼",
  error,
  hints,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggle: () => void;
  autoComplete?: string;
  placeholder?: string;
  /** Validation message shown under the field. */
  error?: string;
  /** Requirement checklist rendered under the field (new-password fields). */
  hints?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-[6px]">
      <label htmlFor={id} style={AUTH_LABEL_STYLE}>
        {label}
      </label>
      <div
        className="flex items-center justify-between gap-[8px] bg-white focus-within:border-[color:var(--green)]"
        style={
          {
            border: `1px solid ${error ? CORE_COLORS.brandRed : "rgba(0,0,0,0.3)"}`,
            borderRadius: "3px",
            padding: "12px 14px",
            height: "48px",
            ["--green" as string]: CORE_COLORS.brandGreen,
          } as React.CSSProperties
        }
      >
        <input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-[color:var(--ph)]"
          style={
            {
              fontFamily: CORE_FONT.sans,
              fontSize: "15px",
              color: CORE_COLORS.textPrimary,
              lineHeight: "22px",
              ["--ph" as string]: AUTH_PLACEHOLDER_COLOR,
            } as React.CSSProperties
          }
        />
        <button
          type="button"
          aria-label={show ? "隱藏密碼" : "顯示密碼"}
          onClick={onToggle}
          // Resting colour via a custom property, applied by a class: an
          // inline `color` outranks every class, so the `hover:text-*` here
          // had never applied and the eye was hover-dead on all four forms.
          className="shrink-0 text-[color:var(--rest-fg)] transition-colors duration-150 hover:text-[color:var(--green)] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[color:var(--green)]"
          style={
            {
              ["--rest-fg" as string]: CORE_COLORS.textSecondary,
              ["--green" as string]: CORE_COLORS.brandGreen,
            } as React.CSSProperties
          }
        >
          {show ? (
            <Eye className="size-[20px]" strokeWidth={1.8} />
          ) : (
            <EyeOff className="size-[20px]" strokeWidth={1.8} />
          )}
        </button>
      </div>
      <FieldError id={id} message={error} />
      {hints}
    </div>
  );
}
